import { getErrorsByField } from 'src/utils';

/**
 * Normalized view of a Bender error, flattening the four structurally different
 * shapes that can reach us through `createBaseQuery`.
 */
export interface NormalizedBenderError {
  /** `undefined` when there was no HTTP response at all (network or CORS failure). */
  status?: number;
  /** Populated only for the class-validator shape, keyed by DTO property name. */
  fieldErrors: Record<string, string>;
  /**
   * Populated for the plain-string shapes. Carries internal detail such as
   * `Failed to provision the refresh schedule for box:409789436486` — log it,
   * never render it. Pick user-facing copy from `status` instead.
   */
  message?: string;
}

/**
 * Untangles Bender's error bodies into one shape.
 *
 * `createBaseQuery` returns `{ status, data: err.response?.data ?? err.message }`,
 * and Bender puts four different things in `data`:
 *
 * 1. class-validator rejections -> `data.message` is an **array** of
 *    `{ property, constraints }` (possibly nested via `children`).
 * 2. `ParseTargetInputPipe` rejections -> `data.message` is a plain **string**.
 * 3. EventBridge `ValidationException` remaps -> `data.message` is a plain **string**.
 * 4. Network / CORS failure -> there is no response, so `data` is the bare axios
 *    message **string** and `status` is `undefined`.
 *
 * Case 4 is the easy one to miss; treating `data` as an object there throws while
 * rendering the error.
 */
export const normalizeBenderError = (error: unknown): NormalizedBenderError => {
  const { status, data } = (error ?? {}) as {
    status?: number;
    data?: string | { message?: unknown };
  };

  // Case 4: no HTTP response — `data` is axios's own message.
  if (typeof data === 'string') {
    return { status, fieldErrors: {}, message: data };
  }

  const message = data?.message;

  // Case 1: class-validator.
  if (Array.isArray(message)) {
    return { status, fieldErrors: getErrorsByField(message as Array<Record<string, unknown>>) };
  }

  // Cases 2 and 3 are indistinguishable by shape, and don't need distinguishing.
  if (typeof message === 'string') {
    return { status, fieldErrors: {}, message };
  }

  return { status, fieldErrors: {} };
};
